import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Loader2, Lightbulb, X, Check } from "lucide-react";

/**
 * Live-kamera med realtidsguidning.
 *
 * Viktigt att vara ärlig om: en telefonkamera kan INTE mäta fukthalt. Det
 * finns ingen sensor i telefonen som ser vatten inne i en vägg. Vad vi kan
 * göra är att hjälpa kunden ta en bild som faktiskt går att bedöma — rätt
 * avstånd, tillräckligt ljus, skarp bild — och läsa av en riktig fuktmätares
 * display. Guidningen nedan mäter ljusstyrka och skärpa i realtid på en liten
 * nedskalad kopia av videobilden, helt i telefonen.
 */

type Mode = "damp" | "meter";

const SAMPLE_W = 128;
const SAMPLE_H = 96;

type Quality = {
  brightness: number;
  sharpness: number;
  ok: boolean;
  hint: string;
};

function analyzeFrame(data: Uint8ClampedArray, w: number, h: number): Quality {
  const luma = new Float32Array(w * h);
  let sum = 0;
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const v = 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!;
    luma[p] = v;
    sum += v;
  }
  const brightness = sum / (w * h);

  // Laplacian-varians = grov skärpemätning. Suddig bild ger låga värden.
  let lapSum = 0;
  let lapSq = 0;
  let n = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const lap =
        4 * luma[i]! - luma[i - 1]! - luma[i + 1]! - luma[i - w]! - luma[i + w]!;
      lapSum += lap;
      lapSq += lap * lap;
      n++;
    }
  }
  const mean = lapSum / n;
  const sharpness = lapSq / n - mean * mean;

  let hint = "Håll still — bilden ser bra ut.";
  let ok = true;
  if (brightness < 45) {
    ok = false;
    hint = "För mörkt. Tänd lampan eller öppna en lucka.";
  } else if (brightness > 225) {
    ok = false;
    hint = "Överexponerat. Lys snett från sidan i stället för rakt på.";
  } else if (sharpness < 55) {
    ok = false;
    hint = "Suddigt. Håll telefonen stilla, ca 25 cm från ytan.";
  }
  return { brightness, sharpness, ok, hint };
}

export function LiveCameraCapture({
  mode,
  onCapture,
  onClose,
}: {
  mode: Mode;
  onCapture: (file: File) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const sampleRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quality, setQuality] = useState<Quality | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);
  const [steady, setSteady] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("no-api");
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (!alive) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        const caps = track?.getCapabilities?.() as { torch?: boolean } | undefined;
        setHasTorch(Boolean(caps?.torch));
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        const name = e instanceof Error ? e.message : "";
        setError(
          name === "no-api"
            ? "Den här telefonen eller webbläsaren släpper inte in kameran. Använd knappen för att fota på vanligt sätt i stället."
            : "Vi fick inte tillgång till kameran. Tillåt kameran i webbläsaren, eller fota på vanligt sätt i stället.",
        );
      }
    })();
    return () => {
      alive = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    let raf = 0;
    let last = 0;
    const tick = (ts: number) => {
      raf = requestAnimationFrame(tick);
      if (ts - last < 250) return;
      last = ts;
      const video = videoRef.current;
      const canvas = sampleRef.current;
      if (!video || !canvas || video.videoWidth === 0) return;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, SAMPLE_W, SAMPLE_H);
      const frame = ctx.getImageData(0, 0, SAMPLE_W, SAMPLE_H);
      const q = analyzeFrame(frame.data, SAMPLE_W, SAMPLE_H);
      setQuality(q);
      setSteady((s) => (q.ok ? Math.min(3, s + 1) : 0));
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ready]);

  const toggleTorch = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({
        advanced: [{ torch: !torchOn }],
      } as unknown as MediaTrackConstraints);
      setTorchOn((v) => !v);
    } catch {
      setHasTorch(false);
    }
  }, [torchOn]);

  const shoot = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((res) =>
      canvas.toBlob(res, "image/jpeg", 0.9),
    );
    if (!blob) return;
    const name = mode === "meter" ? "fuktmatare.jpg" : "livebild.jpg";
    onCapture(new File([blob], name, { type: "image/jpeg" }));
  }, [mode, onCapture]);

  const guideText =
    mode === "meter"
      ? "Rikta rutan mot fuktmätarens display så siffrorna fyller rutan."
      : "Fyll rutan med fläcken eller skarven — ca 25 cm ifrån.";

  return (
    <div className="fixed inset-0 z-[120] flex flex-col bg-foreground/95 backdrop-blur">
      <div className="flex items-center justify-between px-4 py-3 text-background">
        <p className="text-sm font-semibold">
          {mode === "meter" ? "Läs av fuktmätaren" : "Live-kamera"}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Stäng kameran"
          className="rounded-full bg-background/15 p-2"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          playsInline
          muted
          className="h-full w-full object-cover"
        />
        <canvas
          ref={sampleRef}
          width={SAMPLE_W}
          height={SAMPLE_H}
          className="hidden"
        />

        {!ready && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-background">
            <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <p className="max-w-sm rounded-2xl bg-background p-4 text-sm leading-relaxed">
              {error}
            </p>
          </div>
        )}

        {ready && !error && (
          <>
            <div
              aria-hidden="true"
              className={`pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl border-4 transition-colors ${
                mode === "meter" ? "h-32 w-72" : "h-64 w-64"
              } ${steady >= 2 ? "border-emerald-400" : "border-background/70"}`}
            />
            <p className="absolute inset-x-0 top-3 mx-auto max-w-xs rounded-full bg-foreground/70 px-3 py-1.5 text-center text-[12px] font-medium text-background">
              {guideText}
            </p>
            {quality && (
              <p
                className={`absolute inset-x-4 bottom-4 mx-auto max-w-sm rounded-xl px-3 py-2 text-center text-[13px] font-semibold ${
                  quality.ok
                    ? "bg-emerald-500/90 text-white"
                    : "bg-background text-foreground"
                }`}
              >
                {quality.ok ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="h-4 w-4" aria-hidden="true" /> {quality.hint}
                  </span>
                ) : (
                  quality.hint
                )}
              </p>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-center gap-4 px-4 pb-8 pt-4">
        {hasTorch && (
          <button
            type="button"
            onClick={toggleTorch}
            aria-label={torchOn ? "Släck lampan" : "Tänd lampan"}
            className={`rounded-full p-3 ${torchOn ? "bg-background text-foreground" : "bg-background/15 text-background"}`}
          >
            <Lightbulb className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          onClick={shoot}
          disabled={!ready || Boolean(error)}
          className="inline-flex items-center gap-2 rounded-full bg-background px-6 py-3.5 text-sm font-semibold text-foreground disabled:opacity-50"
        >
          <Camera className="h-5 w-5" aria-hidden="true" />
          {mode === "meter" ? "Läs av displayen" : "Ta bilden"}
        </button>
      </div>
    </div>
  );
}
